import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useInfiniteProblems, useProblem } from '@/lib/hooks/useProblems';
import { useAuthoredDrafts } from '@/lib/hooks/useAuthoredDrafts';

const DIFF: Record<string, { label: string; cls: string }> = {
  easy: { label: 'Easy', cls: 'text-green-600 dark:text-green-400' },
  medium: { label: 'Medium', cls: 'text-yellow-600 dark:text-yellow-400' },
  hard: { label: 'Hard', cls: 'text-red-600 dark:text-red-400' },
};

const FILTERS: { key: string; label: string }[] = [
  { key: '', label: 'All' },
  { key: 'easy', label: 'Easy' },
  { key: 'medium', label: 'Medium' },
  { key: 'hard', label: 'Hard' },
];

interface Props {
  /** Currently selected problem slug (empty when none chosen). */
  value: string;
  /** Fired when the trainer picks a problem from the bank. */
  onSelect: (problem: { slug: string; title: string }) => void;
}

/**
 * Searchable problem-bank picker. Queries `/api/v1/problems` server-side (q +
 * difficulty) so search spans the whole 2500+ pool, not a preloaded window.
 *
 * Renders inline (not portaled) so it works inside a Radix Dialog — a portaled
 * popover lands outside the dialog's react-remove-scroll subtree and its list
 * cannot be scrolled.
 */
export function ProblemPicker({ value, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [difficulty, setDifficulty] = useState('');

  const rootRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // Resolve the selected slug → title for the trigger label (edit mode / reopen).
  const { data: current } = useProblem(value);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useInfiniteProblems(debouncedQ, { difficulty });
  const problems = useMemo(() => data?.pages.flatMap((p) => p.problems) ?? [], [data]);
  const total = data?.pages[0]?.total ?? problems.length;

  // The public catalogue only carries published problems, so a trainer's own
  // unpublished work is fetched separately and listed above it.
  const { data: drafts = [] } = useAuthoredDrafts(debouncedQ, difficulty);

  // Debounce the search box before hitting the server.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Infinite scroll: load the next page when the sentinel nears the list bottom.
  useEffect(() => {
    if (!open) return;
    const root = scrollRef.current;
    const target = sentinelRef.current;
    if (!root || !target) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) fetchNextPage();
      },
      { root, rootMargin: '120px' },
    );
    io.observe(target);
    return () => io.disconnect();
  }, [open, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // A selected draft is invisible to the public endpoint `current` resolves
  // against, so fall back to the draft list for its title and difficulty.
  const currentDraft = value ? drafts.find((d) => d.slug === value) : undefined;
  const currentTitle = current?.title ?? currentDraft?.title ?? value;
  const currentDifficulty = current?.difficulty ?? currentDraft?.difficulty;
  const currentDiff = currentDifficulty ? DIFF[currentDifficulty] : undefined;

  const pick = (p: { slug: string; title: string }) => {
    onSelect(p);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        role="combobox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-[#ececec] bg-white px-3 py-2 text-left text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {value ? (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium">{currentTitle}</span>
            <span className="shrink-0 font-mono text-xs text-[#6b7280]">{value}</span>
            {currentDiff && <span className={cn('shrink-0 text-xs font-medium', currentDiff.cls)}>{currentDiff.label}</span>}
            {currentDraft && (
              <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                Draft
              </span>
            )}
          </span>
        ) : (
          <span className="text-[#6b7280]">Search the problem bank…</span>
        )}
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-[#ececec] bg-popover text-popover-foreground shadow-md">
          {/* Search */}
          <div className="flex items-center gap-2 border-b border-[#ececec] px-3">
            <Search className="h-4 w-4 shrink-0 opacity-50" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title or slug…"
              className="h-10 w-full bg-transparent text-sm outline-none placeholder:text-[#6b7280]"
            />
          </div>

          {/* Difficulty filter */}
          <div className="flex items-center gap-1 border-b border-[#ececec] px-2 py-2">
            {FILTERS.map((f) => (
              <button
                key={f.key || 'all'}
                type="button"
                onClick={() => setDifficulty(f.key)}
                className={cn(
                  'rounded-full px-2.5 py-1 text-xs font-medium transition-colors',
                  difficulty === f.key ? 'bg-[#ff5a1f] text-white' : 'text-[#6b7280] hover:bg-[#fafafa]',
                )}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Results */}
          <div ref={scrollRef} className="max-h-64 overflow-y-auto overscroll-contain p-1">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-6 text-sm text-[#6b7280]">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : problems.length === 0 && drafts.length === 0 ? (
              <div className="py-6 text-center text-sm text-[#6b7280]">No problems match your search.</div>
            ) : (
              <>
                {drafts.length > 0 && (
                  <>
                    <p className="px-2 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-[#6b7280]">
                      Your drafts
                    </p>
                    {drafts.map((p) => {
                      const d = DIFF[p.difficulty] ?? { label: p.difficulty, cls: 'text-[#6b7280]' };
                      const isSel = p.slug === value;
                      return (
                        <button
                          key={p.slug}
                          type="button"
                          onClick={() => pick({ slug: p.slug, title: p.title })}
                          className={cn(
                            'flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm transition-colors hover:bg-[#fafafa]',
                            isSel && 'bg-[#f3f4f6]',
                          )}
                        >
                          <Check className={cn('h-4 w-4 shrink-0', isSel ? 'opacity-100' : 'opacity-0')} />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-2">
                              <span className="truncate font-medium">{p.title}</span>
                              <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-800">
                                Draft
                              </span>
                            </span>
                            <span className="block truncate font-mono text-xs text-[#6b7280]">{p.slug}</span>
                          </span>
                          <span className={cn('shrink-0 text-xs font-medium', d.cls)}>{d.label}</span>
                        </button>
                      );
                    })}
                    <p className="px-2 pb-1 pt-2 text-[11px] text-[#6b7280]">
                      Students can&rsquo;t open a draft until you publish it.
                    </p>
                    <div className="my-1 border-t border-[#ececec]" />
                  </>
                )}
                {problems.map((p) => {
                  const d = DIFF[p.difficulty] ?? { label: p.difficulty, cls: 'text-[#6b7280]' };
                  const isSel = p.slug === value;
                  return (
                    <button
                      key={p.slug}
                      type="button"
                      onClick={() => pick({ slug: p.slug, title: p.title })}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left text-sm transition-colors hover:bg-[#fafafa]',
                        isSel && 'bg-[#f3f4f6]',
                      )}
                    >
                      <Check className={cn('h-4 w-4 shrink-0', isSel ? 'opacity-100' : 'opacity-0')} />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate font-medium">{p.title}</span>
                          {p.execution_mode && (
                            <span className="shrink-0 rounded bg-[#f3f4f6] px-1.5 py-0.5 text-[10px] font-medium capitalize text-[#6b7280]">
                              {p.execution_mode}
                            </span>
                          )}
                        </span>
                        <span className="block truncate font-mono text-xs text-[#6b7280]">{p.slug}</span>
                      </span>
                      <span className={cn('shrink-0 text-xs font-medium', d.cls)}>{d.label}</span>
                    </button>
                  );
                })}
                <div ref={sentinelRef} className="h-px" />
                {isFetchingNextPage && (
                  <div className="flex items-center justify-center py-2 text-xs text-[#6b7280]">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  </div>
                )}
                <p className="px-2 py-1.5 text-center text-[11px] text-[#6b7280]">
                  {problems.length} of {total}
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
